import React, { Fragment, useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { fetchTracker } from '../../functions/fetchTracker';
import './loading.css'

import SpinnerImg from '../../assets/spinner.gif';

// How long to wait for the first API request to start after navigation.
// If no API request starts within this window, we hide the loader anyway.
const IDLE_TIMEOUT_MS = 400;

// Hard cap — never show loader longer than this.
const MAX_WAIT_MS = 5000;

export function Loading() {
  const [isLoading, setIsLoading] = useState(true);
  const [fadeOut, setFadeOut] = useState(false);
  const location = useLocation();
  const isFirstMount = useRef(true);

  const hide = (isMounted) => {
    if (!isMounted.current) return;
    setFadeOut(true);
    setTimeout(() => {
      if (isMounted.current) setIsLoading(false);
    }, 600);
  };

  useEffect(() => {
    const isMounted = { current: true };
    setFadeOut(false);
    setIsLoading(true);

    const isFirst = isFirstMount.current;
    isFirstMount.current = false;

    let idleTimer = null;
    let maxTimer = null;
    let unsubscribe = null;
    let seenRequest = false;
    let done = false;

    const finish = () => {
      if (done) return;
      done = true;
      clearTimeout(idleTimer);
      clearTimeout(maxTimer);
      if (unsubscribe) unsubscribe();
      hide(isMounted);
    };

    const startWatchingRequests = () => {
      // If there are already active API calls, wait for them to finish.
      // Otherwise wait IDLE_TIMEOUT_MS for a request to start.
      if (fetchTracker.getCount() > 0) {
        seenRequest = true;
      }

      unsubscribe = fetchTracker.subscribe((count) => {
        if (count > 0) seenRequest = true;
        if (seenRequest && count === 0) finish();
      });

      // If no request starts soon, hide anyway
      idleTimer = setTimeout(() => {
        if (!seenRequest) finish();
      }, IDLE_TIMEOUT_MS);

      // Hard cap
      maxTimer = setTimeout(finish, MAX_WAIT_MS);
    };

    if (isFirst && document.readyState !== 'complete') {
      // Hard reload: wait for window load first, then watch API requests
      const onLoad = () => startWatchingRequests();
      window.addEventListener('load', onLoad, { once: true });
      maxTimer = setTimeout(finish, MAX_WAIT_MS);
      return () => {
        isMounted.current = false;
        done = true;
        clearTimeout(maxTimer);
        window.removeEventListener('load', onLoad);
      };
    }

    // SPA navigation or already-loaded page
    startWatchingRequests();

    return () => {
      isMounted.current = false;
      done = true;
      clearTimeout(idleTimer);
      clearTimeout(maxTimer);
      if (unsubscribe) unsubscribe();
    };
  }, [location.pathname]);

  return (
    <div
      className={fadeOut ? 'fade-out loading-content' : 'loading-content'}
      style={isLoading ? undefined : { display: 'none' }}
    >
      <div className="spinner">
        <img src={SpinnerImg} alt="Loading" />
        <p className="text-center">Loading...</p>
      </div>
    </div>
  );
}
